import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { getAgenticMetadata } from 'services/agentic'
import { findUnknownSelectorTypes } from 'utils/selectorTypes'

export const AGENTIC_METADATA_QUERY_KEY = ['agenticMetadata']

/**
 * Fetches agentic metadata (selector types, context types) to populate selector pickers
 * and validate prompt bodies. Cached for the session rather than refetched per screen.
 * @returns {{
 *   selectorTypes: Array<{name: string, label: string, description: string, example: string}>,
 *   contextTypes: Array<{name: string, label: string, description: string}>,
 *   selectorTypeOptions: Array<{value: string, label: string}>,
 *   selectorTypeNames: Set<string>,
 *   findUnknownSelectors: (body: string) => string[],
 *   isLoading: boolean,
 * }} metadata lists, select options, and selector validation helpers
 */
export function useAgenticMetadata() {
  const { data, isLoading } = useQuery({
    queryKey: AGENTIC_METADATA_QUERY_KEY,
    queryFn: getAgenticMetadata,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
  })

  const selectorTypes = useMemo(() => data?.selectorTypes ?? [], [data?.selectorTypes])
  const contextTypes = useMemo(() => data?.contextTypes ?? [], [data?.contextTypes])

  const selectorTypeOptions = useMemo(
    () =>
      selectorTypes.map(({ name, example, description }) => ({
        value: name,
        label: `${example ?? `{{${name}}}`} — ${description}`,
      })),
    [selectorTypes],
  )

  const selectorTypeNames = new Set(selectorTypes.map(t => t.name))

  // Empty registry means metadata is still loading — suppress warnings rather than flag every placeholder
  const findUnknownSelectors = body =>
    selectorTypeNames.size === 0 ? [] : findUnknownSelectorTypes(body, selectorTypeNames)

  return {
    selectorTypes,
    contextTypes,
    selectorTypeOptions,
    selectorTypeNames,
    findUnknownSelectors,
    isLoading,
  }
}
